import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email adresa je obavezna';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Unesite validnu email adresu';
    }

    if (!formData.password) {
      newErrors.password = 'Lozinka je obavezna';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Lozinka mora imati najmanje 6 karaktera';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Simulate login
      console.log('Login attempt:', formData);
      // Navigate to dashboard
      navigate('/go2-legende/dashboard');
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="bg-gray-50 pt-32 pb-12 min-h-screen">
      {/* Hero section with background */}
      <div
        className="absolute inset-0 h-screen w-full"
        style={{
          backgroundImage: 'url(/assets/go21.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Login card */}
      <div className="relative z-10 max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 border border-gray-100">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/assets/go2-logo.png"
              alt="Go2 Logo"
              className="h-10"
            />
          </div>

          {/* Heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-go2-pink mb-4 text-center">
            Prijavi se na Go2 Legende
          </h1>

          {/* Subtitle */}
          <p className="text-gray-600 text-center mb-8">
            Pristupi svom nalogu i prati bodove, nivoe i nagrade.
          </p>

          {/* Google Login */}
          <button
            type="button"
            onClick={() => navigate('/go2-legende/dashboard')}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-full py-3 px-4 hover:bg-gray-50 transition-colors mb-4"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="font-medium text-gray-700">Prijavi se sa Google</span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">ili sa email-om</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* DEV-only demo quick-fill */}
          {import.meta.env.DEV && (
            <button
              type="button"
              onClick={() => {
                setFormData({ email: 'aleksa@go2travelling.net', password: 'demo123456', rememberMe: true });
                setErrors({});
              }}
              className="w-full text-xs text-go2-blue border border-go2-blue/30 rounded-lg py-2 mb-4 hover:bg-blue-50 transition-colors"
            >
              Demo pristup (aleksa@go2travelling.net / demo123456)
            </button>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email adresa
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="tvoj@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
              />
              {errors.email && (
                <p className="text-red-600 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Lozinka
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Unesi lozinku"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-600 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                  className="w-4 h-4 text-go2-pink border-gray-300 rounded focus:ring-go2-pink"
                />
                <span className="ml-2 text-sm text-gray-700">Zapamti me</span>
              </label>
              <Link
                to="/zaboravljena-lozinka"
                className="text-go2-pink hover:text-brand-600 font-medium text-sm"
              >
                Zaboravljena lozinka?
              </Link>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              className="w-full bg-go2-pink text-white px-8 py-4 rounded-full font-bold text-base uppercase hover:bg-brand-600 transition-all shadow-lg"
            >
              Prijavi se
            </button>
          </form>

          {/* Register link */}
          <p className="text-center mt-6 text-gray-600">
            Nemaš nalog?{' '}
            <Link
              to="/registracija"
              className="text-go2-pink hover:text-brand-600 font-medium"
            >
              Registruj se
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
